import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Banknote } from "lucide-react";
import api, { friendlyError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Card, Skeleton } from "@/ui";

export default function Payouts() {
  const { shopId } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/payouts/summary${shopId ? `?shopId=${shopId}` : ""}`);
      setData(r.data);
    } catch (e) { setError(friendlyError(e)); }
    finally { setLoading(false); }
  }, [shopId]);
  useEffect(() => { if (!shopId) { navigate("/pick-shop", { replace: true }); return; } load(); }, [shopId, navigate, load]);

  const lines = data?.lines || [];

  return (
    <Layout>
      <div style={{ marginBottom: 8 }}>
        <h1 className="t-h1">payouts</h1>
        <p className="muted">what you'll be paid after fees, drawn from your saved sale records — never changed after the fact.</p>
      </div>

      {loading ? (
        <Card><Skeleton style={{ height: 200 }} /></Card>
      ) : error ? (
        <Card data-testid="payouts-error" className="text-sm text-rose-600">{error}</Card>
      ) : (
        <div className="stack" style={{ gap: 16 }}>
          <Card data-testid="payout-summary-card" className="stack" style={{ gap: 8 }}>
            <div className="row" style={{ gap: 8, marginBottom: 4 }}>
              <div className="rounded-full p-2" style={{ background: "var(--sunk)" }}><Banknote size={18} className="text-emerald-600" /></div>
              <div>
                <div className="text-lg font-semibold">Last {data.windowDays} days</div>
                <div className="text-sm text-slate-500">Cash and collected orders, minus the Reto fee.</div>
              </div>
            </div>
            {lines.map((l, i) => (
              <div key={i} data-testid={`payout-line-${i}`}
                className="row-between"
                style={{ padding: "12px 0", borderTop: l.kind === "total" ? "2px solid var(--border)" : "1px solid var(--border)" }}>
                <span className={l.kind === "total" ? "t-h3" : "muted"}>{l.label}</span>
                <span className={`num ${l.kind === "total" ? "t-h3 text-emerald-700" : l.kind === "out" ? "text-rose-600" : ""}`}
                  style={{ fontWeight: l.kind === "total" ? 700 : 500 }}>{l.amount}</span>
              </div>
            ))}
          </Card>
          <p className="muted-3 t-small">Your running balance in the ledger is {data.balance}. Online payouts turn on once payments go live.</p>
        </div>
      )}
    </Layout>
  );
}
